import React, { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import MDBox from "mdComponents/MDBox";
import MDTypography from "mdComponents/MDTypography";
import MDButton from "mdComponents/MDButton";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Rating from "@mui/material/Rating";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormHelperText from "@mui/material/FormHelperText";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import TextField from "@mui/material/TextField";

import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import Footer from "components/Footer";
import MDInput from "mdComponents/MDInput";
import NotificationAlert from "controls/NotificationAlert";
import locationsService from "services/locationsService";
import userRatingService from "services/userRatingService";
import locationSuspendService from "services/locationSuspendService";
import { useUser } from "context/UserContext";
import { formatDate } from "../../Utils";
import { SUSPEND_MEMBER_TIME, SUSPEND_USER_REASONS } from "../../constants";
import { useParams } from "react-router-dom";
import { useHostPortalContext } from "context/HostPortalContext";
import { useNavigationMessageContext } from "context/NavigationMessageContext";
import LoadingSpinner from "controls/LoadingSpinner";

const LocationActivity: React.FC = () => {
  const { user } = useUser();
  const { id } = useParams<{ id: any }>();
  const { host } = useHostPortalContext();
  const { setMessage } = useNavigationMessageContext();
  const [activity, setActivity] = useState<any[] | null>(null);
  const [search, setSearch] = useState("");
  const [selectedActivity, setSelectedActivity] = useState<any>(null);

  const [showModal, setShowModal] = useState(false);
  const [suspendModalOpen, setSuspendModalOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);

  const [userRating, setUserRating] = useState<number>(0);
  const [noteText, setNoteText] = useState("");
  const [pictureLoaded, setPictureLoaded] = useState(false);

  const [suspendAction, setSuspendAction] = useState<"suspend" | "ban">("suspend");
  const [suspendLength, setSuspendLength] = useState("7 days");
  const [suspendReason, setSuspendReason] = useState("");
  const [suspendErrors, setSuspendErrors] = useState<{ [key: string]: string }>({});
  const [savingSuspend, setSavingSuspend] = useState(false);

  const [showSnackBar, setShowSnackBar] = useState(false);
  const [snackbar, setSnackbar] = useState({
    color: "success",
    content: "Saved Successfully!",
    title: "Save Successful",
    icon: "check",
    dateTime: "just now",
  });

  const [reportPicture, setReportPicture] = useState<File | null>(null);
  const [reportLoading, setReportLoading] = useState(false);

  const openSnackBar = () => setShowSnackBar(true);
  const closeSnackBar = () => setShowSnackBar(false);

  useEffect(() => {
    setMessage(`${host?.name} User Visits`);
    return () => setMessage(undefined);
  },[host]);

  useEffect(() => {
    const loadActivity = async () => {
      try {
        const { data } = await locationsService.fetchLocationActivity(host ? host.id : id);
        setActivity(data ?? []);
      } catch (err) {
        console.error(err);
        setActivity([]);
      }
    };
    loadActivity();
  }, [id]);

  const filteredActivity = activity?.filter((item) =>
    `${item.profiles.firstName} ${item.profiles.lastName}`.toLowerCase().includes(search.toLowerCase())
  );

  const handleItemClick = (item: any) => {
    setSelectedActivity(item);
    setUserRating(item.UserRating?.[0]?.rating ?? 0);
    setNoteText(item.UserRating?.[0]?.note ?? "");
    setPictureLoaded(false);
    setShowModal(true);
  };

  const onRateUser = async (newVal: number) => {
    if (!selectedActivity) return;
    const savedRatingId = selectedActivity.UserRating?.[0]?.id ?? -1;
    if (savedRatingId !== -1) return;

    const result = await userRatingService.putUserRating({
      id: savedRatingId,
      hostId: selectedActivity.hostId,
      userId: selectedActivity.userId,
      ratedByUserId: user.id,
      workflowId: selectedActivity.id,
      rating: newVal,
      note: noteText,
    });

    if (result !== -1) {
      setUserRating(newVal);
      setSnackbar({
        color: "success",
        content: "User Rating saved successfully.",
        title: "Save Rating Successful",
        icon: "check",
        dateTime: "just now",
      });
      openSnackBar();
    } else {
      setSnackbar({
        color: "error",
        content: "Error saving rating. Try again.",
        title: "Save Rating Failed",
        icon: "error",
        dateTime: "just now",
      });
      openSnackBar();
    }
  };

  const validateSuspendForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!suspendReason) newErrors.reason = "Reason is required";
    if (!noteText) newErrors.note = "A note is required";
    setSuspendErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveSuspend = async () => {
    if (!selectedActivity || !validateSuspendForm()) return;
    setSavingSuspend(true);

    const lengthInDays = SUSPEND_MEMBER_TIME.find((a) => a.name === suspendLength)?.id ?? 7;

    const payload = {
      userId: selectedActivity.userId,
      hostId: selectedActivity.hostId,
      workflowId: selectedActivity.id,
      isBan: suspendAction === "ban",
      lengthInDays,
      reason: suspendReason,
      notes: noteText,
    };

    const result = await locationSuspendService.insertUserLocationSuspend(payload as any);
    if (result === true) {
      setSnackbar({
        color: "success",
        content: `User has been ${suspendAction === "ban" ? "banned" : "suspended"}`,
        title: "Action Successful",
        icon: "check",
        dateTime: "just now",
      });
      openSnackBar();
      setSuspendModalOpen(false);
      setShowModal(false);
    } else {
      setSnackbar({
        color: "error",
        content: "Failed to save. Please try again.",
        title: "Action Failed",
        icon: "error",
        dateTime: "just now",
      });
      openSnackBar();
    }

    setSavingSuspend(false);
  };

  const submitReport = async () => {
    if (!selectedActivity || !reportPicture) return;
    setReportLoading(true);

    const formData = new FormData();
    formData.append("workflowId", selectedActivity.id);
    formData.append("hostId", selectedActivity.hostId);
    formData.append("userId", selectedActivity.userId);
    formData.append("picture", reportPicture);

    const result = await locationsService.insertPoorConditionReport(formData);

    if (result) {
      setSnackbar({
        color: "success",
        content: "User has been reported",
        title: "Report Submitted",
        icon: "check",
        dateTime: "just now",
      });
      openSnackBar();
      setReportModalOpen(false);
    } else {
      setSnackbar({
        color: "error",
        content: "Error submitting report. Try again.",
        title: "Report Failed",
        icon: "error",
        dateTime: "just now",
      });
      openSnackBar();
    }

    setReportLoading(false);
  };

  if (!activity) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <Grid container spacing={6} justifyContent="center">
            <Grid item xs={12} display="flex" justifyContent="center">
              <Card sx={{ p: 3, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <LoadingSpinner title="Loading Location Activity..." size={30} />
              </Card>
            </Grid>
          </Grid>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pb={3}>
          <Grid container justifyContent="center">
            <Grid item xs={12} xl={12}>
                <MDBox display="flex" alignItems="center" mb={1}>
                  <MDTypography variant="h6">{`Most recent 100 app user visits at ${host?.name ?? 'Location'}`}</MDTypography>

                  <MDBox width="12rem" ml="auto">
                    <MDInput
                      placeholder="Search visitor..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </MDBox>
                </MDBox>
                {filteredActivity.map((item) => (
                  <MDBox
                    key={item.id}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    p={2}
                    mb={1}
                    sx={{
                      backgroundColor: "#f9f9f9",
                      borderRadius: "8px",
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "#efefef" },
                    }}
                    onClick={() => handleItemClick(item)}
                  >
                    <MDBox display="flex" alignItems="center" gap={2}>
                      <img
                        src={item.profiles.profilePic}
                        alt={`${item.profiles.firstName} ${item.profiles.lastName}`}
                        width={50}
                        height={50}
                        style={{ borderRadius: "50%", objectFit: "cover" }}
                      />
                      <MDBox>
                        <MDTypography variant="h6">
                          {item.profiles.firstName} {item.profiles.lastName}
                        </MDTypography>
                        <MDTypography variant="body2">
                          {formatDate(item.startTime, true)}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                ))}
            </Grid>
          </Grid>
      </MDBox>
        {(!filteredActivity || filteredActivity.length == 0) &&
        <Grid container spacing={6} justifyContent="center">
            <Grid item xs={12} display="flex" justifyContent="center">
                <Card sx={{ p: 2, display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <MDBox display="flex" justifyContent="center" alignItems="center">
                        <MDTypography variant="h6">No activity for this location.</MDTypography>
                    </MDBox>
                </Card>
            </Grid>
        </Grid>
        }
      {/* ACTIVITY DETAILS MODAL */}
      <Dialog open={showModal} onClose={() => setShowModal(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {selectedActivity?.profiles.firstName} {selectedActivity?.profiles.lastName}
        </DialogTitle>
        <DialogContent>
          {selectedActivity && (
            <>
              {selectedActivity.postUsePicture && (
                <MDBox mb={2}>
                  {!pictureLoaded && <MDTypography>Loading picture...</MDTypography>}
                  <MDBox
                    component="img"
                    src={selectedActivity.postUsePicture}
                    onLoad={() => setPictureLoaded(true)}
                    sx={{ width: "100%", maxWidth: 400, borderRadius: "12px" }}
                  />
                </MDBox>
              )}

              <MDTypography variant="body2" mb={1}>
                Visit: {formatDate(selectedActivity.startTime)}
              </MDTypography>

              <MDTypography variant="body2" mb={1}>
                Rating:
                <Rating
                  value={userRating}
                  onChange={(_, val) => onRateUser(val || 0)}
                  disabled={selectedActivity.UserRating?.[0]?.id !== undefined}
                />
              </MDTypography>

              <MDInput
                multiline
                rows={5}
                value={noteText}
                onChange={(e: any) => setNoteText(e.target.value)}
                placeholder="Enter a rating note..."
                disabled={selectedActivity.UserRating?.[0]?.id !== undefined}
                fullWidth
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="secondary" onClick={() => setShowModal(false)}>
            Close
          </MDButton>
          {/* <MDButton color="dark" onClick={() => setReportModalOpen(true)}>
            Report Poor Conditions
          </MDButton> */}
          <MDButton color="error" onClick={() => setSuspendModalOpen(true)}>
            Suspend / Ban User
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* SUSPEND MODAL */}
      <Dialog open={suspendModalOpen} onClose={() => setSuspendModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {suspendAction === "suspend" ? "Suspend" : "Ban"} {selectedActivity?.profiles.firstName}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 2, px: 3 }}>
          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <RadioGroup
              row
              value={suspendAction}
              onChange={(e) => setSuspendAction(e.target.value as "suspend" | "ban")}
            >
              <FormControlLabel value="suspend" control={<Radio />} label="Suspend" />
              <FormControlLabel value="ban" control={<Radio />} label="Ban" />
            </RadioGroup>
          </FormControl>

          {suspendAction === "suspend" && (
            <FormControl fullWidth sx={{ mb: 3, minHeight: 60 }} error={!!suspendErrors.time}>
              <InputLabel>Suspend Length</InputLabel>
              <Select value={suspendLength} onChange={(e) => setSuspendLength(e.target.value)} sx={{ minHeight: 45 }}>
                {SUSPEND_MEMBER_TIME.map((item) => (
                  <MenuItem key={item.id} value={item.name}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{suspendErrors.time}</FormHelperText>
            </FormControl>
          )}

          <FormControl fullWidth sx={{ mb: 3, minHeight: 60 }} error={!!suspendErrors.reason}>
            <InputLabel>Reason</InputLabel>
            <Select value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} sx={{ minHeight: 45 }}>
              {SUSPEND_USER_REASONS.map((reason) => (
                <MenuItem key={reason.id} value={reason.name}>
                  {reason.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{suspendErrors.reason}</FormHelperText>
          </FormControl>

          <TextField
            label="Notes"
            multiline
            rows={4}
            fullWidth
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            error={!!suspendErrors.note}
            helperText={suspendErrors.note}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setSuspendModalOpen(false)} variant="outlined" color="secondary" >
            Cancel
          </MDButton>
          <MDButton onClick={saveSuspend} color="error">
            Save
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* REPORT POOR CONDITIONS MODAL */}
      <Dialog open={reportModalOpen} onClose={() => setReportModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Report Poor Conditions</DialogTitle>
        <DialogContent>
          {reportLoading ? (
            <MDTypography>Submitting report...</MDTypography>
          ) : (
            <>
              <MDTypography mb={2}>
                {selectedActivity?.profiles.firstName} {selectedActivity?.profiles.lastName} visited on {formatDate(selectedActivity?.startTime)}
              </MDTypography>

              {reportPicture && (
                <MDBox mb={2}>
                  <img
                    src={URL.createObjectURL(reportPicture)}
                    alt="Report"
                    style={{ width: "100%", maxHeight: 400, objectFit: "contain" }}
                  />
                  <MDButton color="info" onClick={() => setReportPicture(null)}>Re-take Picture</MDButton>
                </MDBox>
              )}

              {!reportPicture && (
                <MDButton variant="contained" component="label" color="info">
                  Upload Picture
                  <input type="file" accept="image/*" hidden onChange={(e) => {
                    if (e.target.files && e.target.files[0]) setReportPicture(e.target.files[0]);
                  }} />
                </MDButton>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="secondary" onClick={() => setReportModalOpen(false)}>Cancel</MDButton>
          <MDButton color="error" disabled={!reportPicture} onClick={submitReport}>Submit Report</MDButton>
        </DialogActions>
      </Dialog>

      <NotificationAlert
        open={showSnackBar}
        onClose={closeSnackBar}
        close={closeSnackBar}
        color={snackbar.color}
        icon={snackbar.icon}
        title={snackbar.title}
        content={snackbar.content}
        dateTime={snackbar.dateTime}
      />

      <Footer />
    </DashboardLayout>
  );
};

export default LocationActivity;
